import { DocumentIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import AttachmentPreview from 'components/AttachmentPreview/AttachmentPreview';
import Button, { ButtonVariant } from 'components/Button/Button';
import FormControls from 'components/FormControls/FormControls';
import MediaUploader, {
  MediaType,
} from 'components/MediaUploader/MediaUploader';
import Panel from 'components/Panel/Panel';
import Placeholder from 'components/Placeholder/Placeholder';
import TextArea from 'components/TextArea/TextArea';
import useCreateSupplementalMaterials from 'hooks/useCreateSupplementalMaterials';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';

enum FormFields {
  NAME = 'name',
  DESCRIPTION = 'description',
}

interface FormData {
  [FormFields.NAME]: string;
  [FormFields.DESCRIPTION]: string;
}

const ApplicantDownloadForm = () => {
  const [showForm, setShowForm] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<Blob | undefined>();
  const { interviewUid } = useParams();
  const { createSupplementalMaterials, isLoading } =
    useCreateSupplementalMaterials();
  const queryClient = useQueryClient();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const [showVirusError, setVirusError] = useState(false);
  const [virusErrorText, setVirusErrorText] = useState('');

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.NAME]: yup
          .string()
          .required(t('Applicant download name is required'))
          .max(50, t('Applicant download name cannot exceed 50 characters')),
        [FormFields.DESCRIPTION]: yup
          .string()
          .required(t('Applicant download description is required'))
          .max(
            300,
            t('Applicant download description cannot exceed 300 characters')
          ),
      }),
    [FormFields.NAME, FormFields.DESCRIPTION]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    values: {
      [FormFields.NAME]: '',
      [FormFields.DESCRIPTION]: '',
    },
  });

  const handleCancel = async () => {
    setShowForm(false);
    setFileUploaded(undefined);
    setVirusError(false);
    setVirusErrorText('');
    reset();
  };

  const onSubmit: SubmitHandler<FormData> = async ({ name, description }) => {
    if (!interviewUid || !fileUploaded) return;
    await createSupplementalMaterials(
      {
        interviewUid,
        name,
        description,
        file: fileUploaded,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Supplemental material added'),
            t('Applicant download successfully added')
          );
          queryClient.invalidateQueries({
            queryKey: ['getSupplementalMaterials', interviewUid],
          });
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const cause: any = error?.cause;
          /* eslint-enable @typescript-eslint/no-explicit-any */
          if (
            cause?.status === 422 &&
            error?.message ===
              'Due to security concerns, we were unable to upload your file. Please review and try again.'
          ) {
            setVirusError(true);
            setVirusErrorText(t(error.message));
            setFileUploaded(undefined);
          } else {
            showErrorNotification(t('Error'), error.message);
          }
        },
      }
    );
  };

  return (
    <div className="relative">
      {showForm ? (
        <form
          data-testid="applicant-download-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <fieldset>
            <Panel isFocused={isDirty} panelClassName="space-y-0">
              <TextArea
                label={t('Name')}
                id={`download-${FormFields.NAME}`}
                value={watch(FormFields.NAME)}
                data-testid="name"
                errorDataTestId="nameError"
                errors={errors[FormFields.NAME]}
                {...register(FormFields.NAME)}
              />
              <TextArea
                label={t('Instructions')}
                id={`download-${FormFields.DESCRIPTION}`}
                value={watch(FormFields.DESCRIPTION)}
                data-testid="instructions"
                errorDataTestId="instructionsError"
                errors={errors[FormFields.DESCRIPTION]}
                {...register(FormFields.DESCRIPTION)}
              />
              <div className="prose-title-h5 pb-2">{t('File')}</div>
              <FormFieldError
                isVisible={showVirusError}
                message={virusErrorText}
                dataTestId="fileUploadError"
              />
              {fileUploaded ? (
                <AttachmentPreview file={fileUploaded} />
              ) : (
                <MediaUploader
                  acceptFileType=".pdf, .txt, .docx, .zip"
                  description={t('Upload a file or zip file under 50MB')}
                  fileSizeLimit={50}
                  isLoading={isLoading}
                  buttonText={t('Upload File')}
                  emptyDescription={t('No file upload yet')}
                  mediaType={MediaType.DOCUMENT}
                  onDelete={handleCancel}
                  onUpload={(data: Blob) => setFileUploaded(data)}
                />
              )}
              <FormControls
                isLoading={isLoading}
                onCancel={handleCancel}
                isDisabled={!fileUploaded}
              />
            </Panel>
          </fieldset>
        </form>
      ) : (
        <Placeholder icon={<DocumentIcon />} title={t('No material added yet')}>
          <Button
            variant={ButtonVariant.SECONDARY}
            buttonText={t('Create Applicant Download')}
            onClick={() => setShowForm(true)}
            data-testid="applicantDownload"
          />
        </Placeholder>
      )}
    </div>
  );
};
export default ApplicantDownloadForm;
